import { Typography } from "@mui/material";
import { sumBy } from "@sinch/utils/array";
import { head } from "ramda";
import React, { useMemo } from "react";
import { match, P } from "ts-pattern";
import {
  ExtendedRateComposition,
  useTreeQueries,
} from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeQueries";
import {
  buildEntityRow,
  ChildrenGetter,
  EntityName,
  LabelGetter,
  TableItemsSource,
} from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/rowBuilders";
import {
  RatesCompositionTableProps,
  Row,
} from "@sinch/components/application/RatesCompositionTable/RatesCompositionTable";
import { CATEGORY_KEY, MaterCategory } from "@sinch/components/application/RatesCompositionTable/types";

const buildRow = (
  serverData: {
    label: string | null;
    data: TableItemsSource<ExtendedRateComposition>[] | null | undefined;
  }[],
  buildRowProps: {
    entityName: EntityName;
    parent: string[];
    labelGetter?: LabelGetter;
    childrenGetter: ChildrenGetter;
  }
) => {
  if (serverData.length <= 1 && head(serverData)?.label === null) {
    return buildEntityRow({ ...buildRowProps, items: head(serverData)?.data ?? [] });
  }
  return buildEntityRow({
    childrenGetter: (data, entityName: EntityName | MaterCategory) => {
      if (entityName !== CATEGORY_KEY) {
        return buildRowProps.childrenGetter(data, entityName as EntityName);
      }
      return {
        childrenList: data.items as TableItemsSource<ExtendedRateComposition>[],
        childrenEntityName: buildRowProps.entityName,
      };
    },
    labelGetter: (data, entityName: EntityName | MaterCategory) => {
      if (entityName !== CATEGORY_KEY) {
        return buildRowProps.labelGetter?.(data, entityName as EntityName);
      }
      return (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: theme.typography.pxToRem(15),
            fontWeight: theme.typography.fontWeightBold,
          })}
          variant="h1"
        >
          {data.id as string}
        </Typography>
      );
    },
    items: serverData.map(({ label, data }) => ({
      id: label ?? "",
      wageComposition: {
        total: sumBy("total", data?.flatMap(({ wageComposition }) => wageComposition ?? { total: 0 }) ?? []),
        components: [],
      },
      priceComposition: {
        total: sumBy("total", data?.flatMap(({ priceComposition }) => priceComposition ?? { total: 0 }) ?? []),
        components: [],
      },
      items: data ?? [],
    })),
    parent: [],
    entityName: CATEGORY_KEY,
  });
};

export const useTreeRows = (
  ids: RatesCompositionTableProps["ids"],
  treeQueries: Omit<ReturnType<typeof useTreeQueries>, "loading">,
  childrenGetter: ChildrenGetter,
  labelGetter: LabelGetter
): Row[] =>
  useMemo(() => {
    const { orderTreeQuery, jobTreeQuery, shiftTreeQuery, positionTreeQuery } = treeQueries;

    const buildArgs = {
      parent: [],
      childrenGetter,
      labelGetter,
    };

    const result = match(ids)
      .with({ positionId: P.nonNullable }, () =>
        buildRow(positionTreeQuery, {
          ...buildArgs,
          entityName: "Position",
        })
      )
      .with({ shiftId: P.nonNullable }, () =>
        buildRow(shiftTreeQuery, {
          ...buildArgs,
          entityName: "Shift",
        })
      )
      .with({ jobId: P.nonNullable }, () =>
        buildRow(jobTreeQuery, {
          ...buildArgs,
          entityName: "Job",
        })
      )
      .with({ orderId: P.nonNullable }, () =>
        buildRow(orderTreeQuery, {
          ...buildArgs,
          entityName: "Order",
        })
      )
      .otherwise(() => ({ rows: [] }));

    return result.rows;
  }, [ids, treeQueries, childrenGetter, labelGetter]);
